import { RadioGroup, type RadioGroupResult } from '@myosh/odin-components'
import { cloneDeep, sortBy } from 'lodash'
import { type HierarchyType, useHierarchyValuesQuery } from '../../../../API/rtk-query-api/store/tmsApi'
import { useHierarchies } from '../../../../mixins/useHierarchies'
import { filtersActions } from '../../../../redux/actions'
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks'
import Checkbox, { type CheckboxChangeEvent } from '../../../UI/checkbox/Checkbox'
import LoadingSpinner from '../../../UI/loading-spinner'
import { type DropdownOption, MultiSelectDropdown } from '../../../UI/multi-select-dropdown/MultiSelectDropdown'

const HierarchyOptionsTM = () => {
  // State
  const dispatch = useAppDispatch()
  const trainingMatrixFilters = useAppSelector(state => state.trainingMatrixFilters)

  const { hierarchies, isLoading: isLoadingHierarchies, isError: isHierarchyError } = useHierarchies()
  const { data: hierarchyValues, isFetching: isFetchingHierarchyValues } = useHierarchyValuesQuery(undefined, {
    skip: !hierarchies || (hierarchies && hierarchies.length === 0)
  })

  // Handlers
  const handleViewModeChange = (selection?: RadioGroupResult) => {
    if (selection) {
      dispatch(filtersActions.setSelectedHierarchyViewMode(selection))
    }
  }

  const handleApplyHierarchyFiltersToOccupationalChange = (selection?: CheckboxChangeEvent) => {
    dispatch(filtersActions.setApplyHierarchyFiltersToOccupationRequirements(selection?.checked ?? false))
  }

  const handleHierarchyValueChange = (selection: Array<DropdownOption>, { id }: HierarchyType) => {
    let isHierarchyValueUpdated = false
    const selectedHierarchies = cloneDeep(trainingMatrixFilters.selectedHierarchies)

    for (let i = 0; i < selectedHierarchies.length; i++) {
      const hierarchy = selectedHierarchies[i]
      if (hierarchy.hierarchyKey === id) {
        hierarchy.values = selection.map(s => s.value)
        isHierarchyValueUpdated = true
      }
    }

    if (!isHierarchyValueUpdated) {
      selectedHierarchies.push({ hierarchyKey: id, values: selection.map(s => s.value) })
    }

    dispatch(filtersActions.setSelectedHierarchies(selectedHierarchies))
  }

  const handleHierarchyFilterSelectAll = (
    hierarchyCategoryId: string,
    hierarchyCategoryOptions: Array<DropdownOption>
  ) => {
    const selectedHierarchies = [...trainingMatrixFilters.selectedHierarchies]
    const hierarchyKey = selectedHierarchies.findIndex(hierarchy => hierarchy.hierarchyKey === hierarchyCategoryId)

    const updatedValues = {
      hierarchyKey: hierarchyCategoryId,
      values: hierarchyCategoryOptions.map(s => s.value)
    }

    if (hierarchyKey > -1) {
      selectedHierarchies[hierarchyKey] = updatedValues
    } else {
      selectedHierarchies.push(updatedValues)
    }

    dispatch(filtersActions.setSelectedHierarchies(selectedHierarchies))
  }

  const isLoading = isLoadingHierarchies || isFetchingHierarchyValues
  const hasHierarchies = hierarchies && hierarchies.length > 0

  const createHierarchyDropdowns = () => {
    if (hasHierarchies && hierarchyValues && hierarchyValues.length > 0) {
      return hierarchies.map(category => {
        const categoryId = category.id ?? ''
        const categoryValues = sortBy(
          hierarchyValues.find(categoryValue => categoryValue.id === categoryId)?.values || [],
          value => value.toLowerCase()
        )

        const categoryDropdownOptions = categoryValues.map(category => {
          return {
            value: category,
            text: category
          }
        })
        const selectedHierarchyOptions = trainingMatrixFilters.selectedHierarchies
          .find(hierarchy => hierarchy.hierarchyKey === categoryId)
          ?.values?.map(value => {
            return { text: value, value: value } as DropdownOption
          })

        return (
          <div key={category.id} style={{ marginBottom: '1.5rem' }}>
            <MultiSelectDropdown
              options={categoryDropdownOptions}
              values={selectedHierarchyOptions ?? []}
              onChange={(selection: Array<DropdownOption>) => handleHierarchyValueChange(selection, category)}
              onSelectAll={() => handleHierarchyFilterSelectAll(categoryId, categoryDropdownOptions)}
              label={category.name}
              placeholder={`Select ${category.name?.toLowerCase()}`}
              appendTo='self'
              fullWidth
            />
          </div>
        )
      })
    }
  }

  return (
    <>
      {isLoading && (
        <div style={{ display: 'flex', justifyContent: 'center', alignContent: 'center' }}>
          <LoadingSpinner />
        </div>
      )}
      {!isLoading && isHierarchyError && <p>Error retrieving hierarchies, refresh the page or try again later</p>}
      {!isLoading && hasHierarchies && (
        <>
          {createHierarchyDropdowns()}
          <RadioGroup
            label='Match'
            orientation='HORIZONTAL'
            name='match'
            textField='text'
            valueField='value'
            data={[
              { position: 1, text: 'All', value: 'all' },
              { position: 2, text: 'Any', value: 'any' }
            ]}
            onChange={handleViewModeChange}
            value={trainingMatrixFilters.hierarchyViewMode}
          />
          <Checkbox
            label='Apply Hierarchy filters to Occupational Requirements'
            name='applyHierarchyFiltersToOccupational'
            onChange={handleApplyHierarchyFiltersToOccupationalChange}
            checked={trainingMatrixFilters.applyHierarchyFiltersToOccupationRequirements}
          />
        </>
      )}
    </>
  )
}

export default HierarchyOptionsTM
